import React from 'react';

/**
 * Generic React error boundary with a friendly, theme-aware fallback so a
 * render error in a page shows a recoverable message instead of blanking the
 * whole app. Wrap it around the route content (the <Outlet/>); the surrounding
 * shell (header / nav / footer) keeps working.
 *
 * Reset behaviour:
 *  - auto-clears when `resetKey` changes (pass the route path) so navigating
 *    away from a broken page lets the next page render;
 *  - "Try again" re-renders the same children (good for transient errors).
 *
 * Optional props: title, message, retryLabel, homeLabel (for i18n).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface for diagnostics; swap for a monitoring service call in production.
    console.error('ErrorBoundary caught:', error, info && info.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 14,
          minHeight: '40vh',
          padding: '48px 20px',
          background: 'var(--background)',
          color: 'var(--main-text-color)',
        }}
      >
        <div aria-hidden="true" style={{ fontSize: 48, lineHeight: 1 }}>⚠️</div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          {this.props.title || 'Something went wrong'}
        </h2>
        <p style={{ margin: 0, maxWidth: 420, color: 'var(--secondaryText)', fontSize: 15 }}>
          {this.props.message ||
            'This page hit an unexpected error. You can try again, or go back to the home page.'}
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 8 }}>
          <button
            type="button"
            onClick={this.handleRetry}
            style={{
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              background: 'var(--surface)',
              color: 'var(--main-text-color)',
              borderRadius: 22,
              padding: '10px 22px',
              fontFamily: 'Geologica',
              fontSize: 15,
            }}
          >
            {this.props.retryLabel || 'Try again'}
          </button>
          <a
            href="/"
            style={{
              textDecoration: 'none',
              cursor: 'pointer',
              background: 'var(--main-red)',
              color: 'var(--white)',
              borderRadius: 22,
              padding: '10px 22px',
              fontFamily: 'Geologica',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            {this.props.homeLabel || 'Go to home'}
          </a>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
